import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { InactiveCaseAlert } from '@/components/ui/InactiveCaseAlert';
import { Loader2, CheckCircle, XCircle, Bell } from 'lucide-react';
import { birthdayNotificationService } from '@/services/birthdayNotificationService';
import { clearNotificationCache } from '@/utils/clearNotificationCache';

interface InactiveCase {
  case_id: string;
  case_title: string;
  client_id: string;
  client_name: string;
  client_age: number;
  inactivity_days: number;
  last_movement_date: string;
  priority_status: string;
}

const TestPage: React.FC = () => {
  const [inactiveCases, setInactiveCases] = useState<InactiveCase[]>([]);
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState<InactiveCase[]>([]);
  const { toast } = useToast();

  const fetchInactiveCases = async () => {
    setLoading(true);
    setAlerts([]); // Limpa alertas anteriores
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Erro",
          description: "Usuário não autenticado. Faça login para simular.",
          variant: "destructive"
        });
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.rpc('get_inactive_priority_cases', { p_user_id: user.id });

      if (error) throw error;
      setInactiveCases(data || []);

      // Dispara alertas para os casos inativos
      (data || []).forEach((caseItem: InactiveCase) => {
        // Simula a lógica de alerta: João e Ana devem gerar pop-ups
        if (caseItem.client_name.includes("João da Silva") || caseItem.client_name.includes("Ana Oliveira")) {
          setAlerts(prev => [...prev, caseItem]);
        }
      });

      toast({
        title: "Sucesso",
        description: "Verificação de processos inativos concluída.",
        variant: "default"
      });

    } catch (err) {
      console.error('Erro ao buscar processos inativos:', err);
      toast({
        title: "Erro",
        description: `Não foi possível verificar processos inativos: ${err.message || err}`,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewCase = (caseId: string) => {
    toast({
      title: "Ação",
      description: `Navegar para o processo ${caseId} (funcionalidade de navegação real não implementada nesta simulação).`,
    });
    // Aqui você implementaria a navegação real para a página de detalhes do processo
    // Por exemplo: navigate(`/processo/${caseId}`);
  };

  const handleCloseAlert = (id: string) => {
    setAlerts(prev => prev.filter(alert => alert.case_id !== id));
  };

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-3xl font-bold">Simulação de Prioridade e Alertas</h1>
      <p className="text-muted-foreground">Teste a lógica de tramitação prioritária e alertas de inatividade.</p>

      <Card>
        <CardHeader>
          <CardTitle>Controle de Simulação</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col md:flex-row items-center gap-4">
          <Button onClick={fetchInactiveCases} disabled={loading}>
            {loading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle className="mr-2 h-4 w-4" />
            )}
            Executar Checagem de Prioridade e Alertas
          </Button>
          {loading && <p className="text-sm text-muted-foreground">Verificando...</p>}
          {inactiveCases.length > 0 && (
            <p className="text-sm text-muted-foreground">
              Encontrados {inactiveCases.length} processos inativos com prioridade.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Processos Inativos com Prioridade</CardTitle>
        </CardHeader>
        <CardContent>
          {inactiveCases.length === 0 && !loading ? (
            <p className="text-muted-foreground">Nenhum processo inativo com prioridade encontrado ou verificação não executada.</p>
          ) : (
            <div className="space-y-4">
              {inactiveCases.map((caseItem) => (
                <div key={caseItem.case_id} className="border p-4 rounded-md">
                  <p className="font-semibold">Processo: {caseItem.case_title}</p>
                  <p>Cliente: {caseItem.client_name} ({caseItem.client_age} anos)</p>
                  <p>Status de Prioridade: {caseItem.priority_status}</p>
                  <p>Inatividade: {caseItem.inactivity_days} dias</p>
                  <p>Última Movimentação: {new Date(caseItem.last_movement_date).toLocaleDateString('pt-BR')}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Renderiza os alertas interativos */}
      <div className="fixed bottom-4 right-4 space-y-2">
        {alerts.map((alert) => (
          <InactiveCaseAlert
            key={alert.case_id}
            id={alert.case_id}
            clientName={alert.client_name}
            caseTitle={alert.case_title}
            inactivityDays={alert.inactivity_days}
            onViewCase={handleViewCase}
            onClose={handleCloseAlert}
          />
        ))}
      </div>

      {/* Teste de Notificação de Aniversário */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Teste de Notificação de Aniversário
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-4">
            Clique no botão abaixo para testar o sistema de notificações de aniversário.
          </p>
          <Button onClick={async () => {
            try {
              await birthdayNotificationService.testBirthdayNotification();
              toast({
                title: "Teste de Notificação",
                description: "Notificação de aniversário de teste enviada! Verifique se apareceu no navegador.",
              });
            } catch (error) {
              console.error('Erro no teste de notificação:', error);
              toast({
                title: "Erro no Teste",
                description: "Não foi possível enviar a notificação de teste",
                variant: "destructive"
              });
            }
          }}>
            <Bell className="w-4 h-4 mr-2" />
            Testar Notificação de Aniversário
          </Button>
          
          <Button 
            variant="destructive" 
            onClick={() => {
              clearNotificationCache();
              window.location.reload();
              toast({
                title: "Cache Limpo",
                description: "Cache de notificações limpo e página recarregada",
              });
            }}
            className="mt-2"
          >
            🗑️ Limpar Cache de Notificações
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default TestPage;